/**
 * SPA page-view tracking for GTM/GA4.
 *
 * Assembly's own GTM container (id baked in per environment at build time,
 * see index.html + scripts/deploy.sh) is loaded unconditionally by the
 * inline <head> script in index.html, on every venue's site, it feeds
 * that environment's Assembly GA4 property. Because this is a client-routed SPA
 * (TanStack Router), real navigations don't reload the page, so GA4's
 * built-in page_view never re-fires. We push a `virtual_page_view` event to
 * the dataLayer on every route resolution; the GTM container has a Custom
 * Event trigger on `virtual_page_view` that fires the GA4 page-view tag
 * (which itself still sends GA4 the standard `page_view` event name, only
 * the dataLayer signal is renamed). Deliberately NOT named `page_view`:
 * GTM's "Google tag" entity auto-models recognized GA4 event names and
 * re-emits its own `eventModel`-wrapped echo of them to the dataLayer,
 * which a trigger literally named `page_view` would also match, double-
 * firing the tag. `virtual_page_view` isn't a recognized name, so it isn't
 * echoed. Every push also carries `venue_slug` and `venue_type` (set via
 * `setAnalyticsVenue` once the venue resolves) so events in the shared
 * property can be split back out per client, or rolled up by archetype
 * (HOSPITALITY/STUDIO/RECORD_SHOP/FESTIVAL/WELLNESS) across every venue of
 * that type, see docs/GA4-SETUP.md for the custom-dimension registration
 * both fields need before GA4 can filter/report on them.
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import type { router as appRouter } from "../router";
import type { OrderReceiptResponse } from "./types";
import { pingVenuePresence } from "./api";

type DataLayerWindow = Window & {
  dataLayer?: Array<Record<string, unknown>>;
  gtag?: (...args: unknown[]) => void;
};

const CONSENT_KEY = "assembly_consent";

let currentVenueSlug: string | null = null;
// The venue's archetype (HOSPITALITY/STUDIO/RECORD_SHOP/FESTIVAL/WELLNESS),
// stamped on every virtual_page_view so GA4 can slice traffic by archetype
// across the whole platform, not just per-venue via venue_slug.
let currentVenueType: string | null = null;
let pendingGtmContainerId: string | null = null;
let pendingGa4MeasurementId: string | null = null;
const loadedExtraContainers = new Set<string>();
const loadedGa4Properties = new Set<string>();

function hasAnalyticsConsent(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === "granted";
  } catch {
    return false;
  }
}

/**
 * Call once the venue resolves (see main.tsx's Root()). Stamps every
 * subsequent dataLayer push with `venue_slug`, and lays on any self-service
 * tracking the venue has configured (appConfig.analytics), additive to
 * Assembly's own base GTM-MCGBTWJ5, never a replacement for it.
 *
 * Safe to call repeatedly with the same config (e.g. on a react-query
 * refetch), both loads are deduped so they only happen once per distinct
 * value.
 *
 * Also pushes `venue_slug`/`venue_type` as their OWN dataLayer entry (no
 * `event` key) the first time each becomes known, not just stashing them
 * in this module's local variables for our own pushXxx() functions below to
 * read at push-time. GTM's dataLayer is a persistent data model, not just
 * an event bus: a Data Layer Variable resolves from the MOST RECENT push
 * that set that key, regardless of which push fired the current trigger.
 * So once this lands, EVERY tag for the rest of the session can read it,
 * including GA4's own auto-collected Enhanced Measurement events (scroll,
 * outbound click, file download, the native `page_view`) that never go
 * through any of our own pushXxx() calls and so could otherwise never
 * carry it. Requires a matching step in the GTM container itself (define
 * Data Layer Variables named venue_slug/venue_type, then add them as
 * Fields to Set on the GA4 Configuration tag so every hit under it
 * inherits them), that half lives in the GTM UI, not this codebase.
 */
export function setAnalyticsVenue(
  venue: {
    slug: string;
    archetype?: string | null;
    appConfig?: { analytics?: { gtmContainerId?: string; ga4MeasurementId?: string } } | null;
  } | null,
): void {
  if (typeof window === "undefined") return;
  const slug = venue?.slug ?? null;
  const type = venue?.archetype ?? null;
  if (slug !== currentVenueSlug || type !== currentVenueType) {
    const w = window as DataLayerWindow;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ venue_slug: slug ?? undefined, venue_type: type ?? undefined });
  }
  currentVenueSlug = slug;
  currentVenueType = type;

  const cfg = venue?.appConfig?.analytics;
  if (!cfg) return;

  // Client's own additional GTM container, loaded alongside GTM-MCGBTWJ5,
  // never in place of it. GTM supports multiple containers per page.
  //
  // Both this and the GA4 load below are gated on consent: unlike
  // GTM-MCGBTWJ5 (whose own tags are Google-tag types that auto-respect the
  // Consent Mode signal on the shared dataLayer), a client's own GTM
  // container is configured entirely in their GTM UI, a non-Google/
  // custom-HTML tag in there wouldn't respect Consent Mode and could fire
  // before the visitor has answered the ConsentBanner. So we only load
  // either once consent is already granted, and load them immediately on
  // grant if the venue resolved first (see `onAnalyticsConsentGranted`,
  // called from ConsentBanner's Accept).
  if (cfg.gtmContainerId && !loadedExtraContainers.has(cfg.gtmContainerId)) {
    if (hasAnalyticsConsent()) {
      loadedExtraContainers.add(cfg.gtmContainerId);
      loadGtmContainer(cfg.gtmContainerId);
    } else {
      pendingGtmContainerId = cfg.gtmContainerId;
    }
  }

  // Client's own GA4 property, a direct gtag.js connection (the standard
  // "Global Site Tag" install, same as GA4's own setup instructions when
  // you're not using GTM), entirely independent of Assembly's shared
  // property/dashboards and of the client's own GTM container (if any).
  if (cfg.ga4MeasurementId && !loadedGa4Properties.has(cfg.ga4MeasurementId)) {
    if (hasAnalyticsConsent()) {
      loadedGa4Properties.add(cfg.ga4MeasurementId);
      loadGa4Property(cfg.ga4MeasurementId);
    } else {
      pendingGa4MeasurementId = cfg.ga4MeasurementId;
    }
  }
}

/** Call from ConsentBanner when the visitor accepts, to load any client GTM
 * container / GA4 property that was held back pending consent (see
 * `setAnalyticsVenue`). */
export function onAnalyticsConsentGranted(): void {
  if (pendingGtmContainerId && !loadedExtraContainers.has(pendingGtmContainerId)) {
    loadedExtraContainers.add(pendingGtmContainerId);
    loadGtmContainer(pendingGtmContainerId);
  }
  pendingGtmContainerId = null;

  if (pendingGa4MeasurementId && !loadedGa4Properties.has(pendingGa4MeasurementId)) {
    loadedGa4Properties.add(pendingGa4MeasurementId);
    loadGa4Property(pendingGa4MeasurementId);
  }
  pendingGa4MeasurementId = null;
}

function loadGtmContainer(containerId: string): void {
  const w = window as DataLayerWindow;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  document.head.appendChild(script);
}

// Standard gtag.js install for a single GA4 property, scoped to just that
// property (unlike GTM, gtag.js has no concept of "multiple containers", so
// each call here is its own independent <script> + config call). Reuses the
// window.gtag/dataLayer the base index.html script already set up (it runs
// unconditionally in <head>, before this ever fires).
function loadGa4Property(measurementId: string): void {
  const w = window as DataLayerWindow;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
  w.gtag!("js", new Date());
  w.gtag!("config", measurementId);
}

export type EcommerceItem = {
  item_id: string;
  item_name: string;
  item_category: string;
  price: number; // major currency unit (e.g. pounds, not pence), GA4's own convention
  quantity: number;
};

export type EcommercePayload = {
  currency: string;
  value: number; // major currency unit; should equal sum(items[].price * items[].quantity)
  items: EcommerceItem[];
  transaction_id?: string; // purchase only
  coupon?: string;
};

/**
 * Pushes one of GA4's recommended ecommerce events (add_to_cart,
 * begin_checkout, purchase) per its documented Ecommerce contract:
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 * `ecommerce: null` is pushed first, dataLayer.push merges objects rather
 * than replacing them, so without this a stale `items` array from the
 * previous ecommerce push would otherwise leak into this one (GA4's own
 * documented gotcha). Unlike `virtual_page_view` above, these ARE GA4's real
 * recommended event names, Enhanced Measurement's auto-echo (the reason
 * page_view got renamed) only covers auto-collected interactions, not
 * ecommerce events, so there's no name collision to design around here;
 * GTM's "Ecommerce data → Data Layer" tag option reads the `ecommerce`
 * object under these exact standard names.
 */
export function pushEcommerceEvent(
  event: "add_to_cart" | "begin_checkout" | "purchase",
  ecommerce: EcommercePayload,
): void {
  if (typeof window === "undefined") return;
  const w = window as DataLayerWindow;
  const dl = w.dataLayer;
  if (!dl) return;
  dl.push({ ecommerce: null });
  dl.push({
    event,
    ecommerce,
    venue_slug: currentVenueSlug ?? undefined,
    venue_type: currentVenueType ?? undefined,
  });
}

// How long to give GTM to actually fire the tag before navigating anyway,
// matches the `eventTimeout` handed to GTM itself below, so our own
// fallback and GTM's internal one are aligned.
const NAVIGATE_AFTER_PUSH_TIMEOUT_MS = 1000;

/**
 * Like pushEcommerceEvent, but for begin_checkout specifically, which every
 * call site fires immediately before `window.location.href = <stripe url>`.
 * A bare push-then-navigate lets the browser start unloading the page
 * before GTM has actually read the `ecommerce` object out of the dataLayer
 * and sent the GA4 hit, the Custom Event trigger still fires, but the
 * request carrying the parameters can go out incomplete or get cancelled by
 * the navigation (a well-known GA4/GTM failure mode for any event that
 * immediately precedes a redirect). `eventCallback`/`eventTimeout` are
 * dataLayer keys GTM's own Google-tag listener recognizes, it calls
 * `eventCallback` once this event's tags have actually fired, or after
 * `eventTimeout` ms if they haven't. We navigate from that callback instead
 * of immediately, with our own setTimeout as a second fallback in case GTM
 * never calls back at all (container blocked, consent not yet granted, tag
 * paused), either way the buyer is never stuck waiting more than ~1s.
 */
export function pushEcommerceEventThenNavigate(
  event: "begin_checkout",
  ecommerce: EcommercePayload,
  url: string,
): void {
  if (typeof window === "undefined") return;
  if (!url) return; // caller error, don't strand the user on the current page

  // Guarantee navigation. Sanity: multiple failure modes surfaced 2026-09-05
  // where the button stayed stuck on "Starting checkout…" because analytics
  // pushed but nothing navigated (Madruga: `electricmail@gmx.co.uk`, 6 pending
  // orders). Any of these can silently break the flow:
  //   - dataLayer is a proxied getter/setter (some GTM setups, some extensions)
  //     and dl.push throws
  //   - dl is present but push doesn't actually enqueue (broken container)
  //   - setTimeout is deferred by browser throttling on backgrounded tab
  //   - eventCallback never fires (tag paused, consent not granted)
  // Wrap the whole analytics dance in try/catch and always run the go() in a
  // finally, so no matter what, the buyer gets to Stripe.
  let navigated = false;
  const go = () => {
    if (navigated) return;
    navigated = true;
    window.location.href = url;
  };
  try {
    const w = window as DataLayerWindow;
    const dl = w.dataLayer;
    if (dl) {
      dl.push({ ecommerce: null });
      dl.push({
        event,
        ecommerce,
        venue_slug: currentVenueSlug ?? undefined,
        venue_type: currentVenueType ?? undefined,
        eventCallback: go,
        eventTimeout: NAVIGATE_AFTER_PUSH_TIMEOUT_MS,
      });
    }
  } catch (err) {
    // Analytics failed. Not the buyer's problem, still navigate.
    console.warn("[analytics] pushEcommerceEventThenNavigate: dataLayer push failed, navigating anyway", err);
  } finally {
    // Hard fallback — fires regardless of GTM's eventCallback, capped by
    // NAVIGATE_AFTER_PUSH_TIMEOUT_MS so a healthy path still navigates fast.
    setTimeout(go, NAVIGATE_AFTER_PUSH_TIMEOUT_MS);
  }
}

// Canonical labels for the key marketing CTAs, tracked consistently across
// every archetype's site even though the button copy itself varies per
// placement ("Join", "Join the list", "Join the waitlist", "Get my 10% off"
// all map to "subscribe"), so GTM/GA4 can report on CTA engagement without
// needing a trigger per button-text variant.
export type CtaLabel = "subscribe" | "download";

/**
 * Pushes a `cta_click` dataLayer event, fired on the click/tap of the CTA
 * element itself, not gated on whatever happens after (form validation,
 * the mailing-list API call succeeding, "download" clicking through to an
 * app-store listing the visitor may never install from, etc). This is a
 * pure top-of-funnel engagement signal, distinct from an actual conversion
 *, see `pushFormInteraction` for the "subscribe" CTA's conversion event.
 */
export function pushCtaClick(ctaLabel: CtaLabel): void {
  if (typeof window === "undefined") return;
  const w = window as DataLayerWindow;
  const dl = w.dataLayer;
  if (!dl) return;
  dl.push({
    event: "cta_click",
    cta_label: ctaLabel,
    venue_slug: currentVenueSlug ?? undefined,
    venue_type: currentVenueType ?? undefined,
  });
}

// "success": the joinMailingList() call resolved, the conversion
// counterpart to cta_click("subscribe"). "fail": it reached the server and
// got rejected (network error, or the API 400/500s, duplicate address,
// blocklisted domain, a malformed address the input's basic type="email"
// pattern let through, a rate limit, etc). NOT pushed for addresses the
// browser's own required/type="email" constraint validation rejects before
// the form's submit event ever fires, that's blocked client-side with no
// JS involved, so there's no hook to observe it from (a noValidate form +
// hand-rolled validation would be a bigger change than this warrants for
// now). More statuses (e.g. "start") can extend this union later without
// touching call sites that only ever push "success" | "fail".
export type FormStatus = "success" | "fail";

/**
 * Pushes a `form_interaction` dataLayer event with a `form_status`
 * parameter, one event shape for any form's outcome, rather than a
 * separate event name per form per status (the earlier subscribe_success/
 * subscribe_fail pair this replaces). A GTM trigger on
 * `event equals form_interaction` catches every form's result in one
 * place; filter on `form_status` to split success vs fail, and compare
 * against `cta_click` where `cta_label` equals "subscribe" for
 * click-to-conversion rate. No "download" counterpart exists, clicking
 * through to an app-store listing has no in-app confirmation that an
 * install (or even a page view) actually happened, so there's nothing
 * reliable to gate a result on.
 */
export function pushFormInteraction(status: FormStatus): void {
  if (typeof window === "undefined") return;
  const w = window as DataLayerWindow;
  const dl = w.dataLayer;
  if (!dl) return;
  dl.push({
    event: "form_interaction",
    form_status: status,
    venue_slug: currentVenueSlug ?? undefined,
    venue_type: currentVenueType ?? undefined,
  });
}

/**
 * Converts a human label ("What's On", "Venue Hire") to the snake_case
 * identifier `pushNavigation`'s `menu_item` uses ("whats_on", "venue_hire")
 *, apostrophes are dropped rather than turned into an underscore (so
 * "What's On" reads as "whats_on", not the uglier "what_s_on"), then any
 * other run of non-alphanumeric characters collapses to one "_".
 */
export function toSnakeCaseLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/'/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Pushes a `navigation` dataLayer event with a `menu_item` parameter, the
 * shared shape for site-chrome navigation tracking going forward, not just
 * this one menu. Callers pass an already-snake_case value (see
 * `toSnakeCaseLabel`) so `menu_item` stays a stable identifier even if the
 * on-screen label copy changes. The burger/menu-toggle button itself uses
 * the fixed value "main_menu" (opening the menu isn't navigating to a page,
 * so it isn't one of the page labels).
 */
export function pushNavigation(menuItem: string): void {
  if (typeof window === "undefined") return;
  const w = window as DataLayerWindow;
  const dl = w.dataLayer;
  if (!dl) return;
  dl.push({
    event: "navigation",
    menu_item: menuItem,
    venue_slug: currentVenueSlug ?? undefined,
    venue_type: currentVenueType ?? undefined,
  });
}

const purchaseFiredKey = (id: string) => `assembly:ga_purchase:${id}`;

function hasFiredPurchase(id: string): boolean {
  try {
    return sessionStorage.getItem(purchaseFiredKey(id)) != null;
  } catch {
    return false;
  }
}

/**
 * Fires GA4's `purchase` event exactly once per order/session id, for a
 * confirmation page that only has that id (from the Stripe success_url) and
 * needs to fetch the rest. Polls `fetchReceipt` for a short window the same
 * way tickets.tsx already polls for the ticket list, the webhook that
 * flips an order to "paid" is async and may not have landed the instant the
 * buyer lands back here.
 *
 * The dedup guard lives here, in app code, keyed on a real transaction id,
 * NOT in GTM. See docs/SESSION-LOG.md's 2026-07-28 purchase-pixel dedup
 * write-up: the previous design had no real id to key on (`?purchased=1`),
 * so the workaround tried to live inside GTM as a Custom JS Variable read
 * inside a trigger's own condition, which turned out to never be invoked
 * (a real GTM bug, confirmed in Preview), so the tag kept double-firing on
 * refresh/back-forward regardless. Now that every checkout flow's
 * success_url carries the real order/session id, the guard has a
 * meaningful key and can live somewhere that actually works: sessionStorage,
 * checked before the push ever reaches the dataLayer, so GTM only ever sees
 * one `purchase` event per completed order in the first place.
 */
export function usePurchaseEvent(
  id: string | null,
  fetchReceipt: (id: string) => Promise<OrderReceiptResponse>,
): void {
  const alreadyFired = id ? hasFiredPurchase(id) : true;

  const q = useQuery({
    queryKey: ["purchase-receipt", id],
    queryFn: () => fetchReceipt(id!),
    enabled: !!id && !alreadyFired,
    retry: false,
    refetchInterval: (query) => {
      const data = query.state.data as OrderReceiptResponse | undefined;
      return data && !data.ready && query.state.dataUpdateCount < 8 ? 2000 : false;
    },
  });

  useEffect(() => {
    if (!id || alreadyFired || !q.data?.ready) return;
    if (hasFiredPurchase(id)) return; // race guard: another mount already fired
    try {
      sessionStorage.setItem(purchaseFiredKey(id), "1");
    } catch {
      // sessionStorage unavailable (private mode etc.), proceed best-effort
      // rather than block the event on a guard we can't persist anyway.
    }
    const { transactionId, currency, value, items, coupon } = q.data;
    pushEcommerceEvent("purchase", { transaction_id: transactionId, currency, value, items, ...(coupon ? { coupon } : {}) });
  }, [id, alreadyFired, q.data]);
}

let lastVirtualPageViewPath = "";

/**
 * First-party pageview → Assembly's OWN collector (/api/embed/track, the same
 * endpoint the external embed uses). This is what makes the "Website traffic"
 * dashboard populate for EVERY Assembly-hosted venue (Madruga, Toka, Love Inn…)
 * by default, not just external embed sites, one owned dataset for the whole
 * roster, no Google in the loop.
 *
 * Same-origin (the SPA runs on the venue's own Assembly host), and cookieless:
 * the server hashes ip+ua+day, nothing is stored on the device. That's why it
 * fires without the GA4/GTM consent gate, it's the minimal, banner-free signal,
 * exactly like the external embed. Admin pages are excluded (that's the operator,
 * not the venue's audience).
 */
// Assembly's OWN marketing hosts, traffic here is attributed to the reserved
// platform scope (slug "assembly" → the platform admin's "Website" tab), so
// Fenton can see who's looking at the Assembly pitch, with the same
// attribution/funnel treatment a venue gets. Mirrors PLATFORM_SLUG in
// src/lib/handlers/embed/track.ts.
const PLATFORM_MARKETING_HOSTS = [
  "assemblynetwork.co.uk",
  "www.assemblynetwork.co.uk",
  "assemblytickets.com",
  "www.assemblytickets.com",
  "aws-prod.assemblytickets.com",
  "aws-staging.assemblytickets.com",
];

function firstPartySlug(): string | null {
  if (currentVenueSlug) return currentVenueSlug;
  return PLATFORM_MARKETING_HOSTS.includes(window.location.hostname.toLowerCase()) ? "assembly" : null;
}

function sendFirstPartyEvent(body: string): void {
  // text/plain → CORS-safelisted → sendBeacon needs no preflight (same
  // reasoning as the embed; same-origin here, but keeps it beacon-reliable).
  if (navigator.sendBeacon && navigator.sendBeacon("/api/embed/track", new Blob([body], { type: "text/plain" }))) return;
  fetch("/api/embed/track", { method: "POST", headers: { "content-type": "text/plain" }, body, keepalive: true }).catch(() => {});
}

function sendFirstPartyPageview(rawPath: string): void {
  const slug = firstPartySlug();
  if (!slug) return; // unrecognized host with no venue, nothing to attribute to
  const path = rawPath.split("?")[0].split("#")[0] || "/";
  // An admin.<domain> host is a client's operator CONSOLE (their real public
  // site is external + tracked by the embed), so NONE of its SPA navigation is
  // audience traffic, not just /admin. Without this, the operator browsing the
  // app (sign-in, noticeboard, benefits…) pollutes the venue's own dashboard.
  if (window.location.hostname.startsWith("admin.")) return;
  if (path === "/admin" || path.startsWith("/admin/")) return; // operator, not audience
  try {
    const params = new URLSearchParams(window.location.search);
    // Ad-click id PRESENCE only, same as the external embed (see
    // embed/analytics.js's adClickFlags), lets a hosted venue's own
    // dashboard attribute a landing hit to an ad platform even without a
    // utm_* tag on the link.
    const has = (name: string) => (params.has(name) ? true : undefined);
    const body = JSON.stringify({
      venue: slug,
      type: "pageview",
      path,
      referrer: document.referrer || undefined,
      utmSource: params.get("utm_source") || undefined,
      utmMedium: params.get("utm_medium") || undefined,
      utmCampaign: params.get("utm_campaign") || undefined,
      gclid: has("gclid"),
      gbraid: has("gbraid"),
      wbraid: has("wbraid"),
      fbclid: has("fbclid"),
      msclkid: has("msclkid"),
      ttclid: has("ttclid"),
      twclid: has("twclid"),
      liFatId: has("li_fat_id"),
    });
    sendFirstPartyEvent(body);
  } catch {
    /* best-effort, analytics must never break navigation */
  }
}

/**
 * First-party pageview for pages rendered OUTSIDE the router (the marketing
 * landing, /for/* sales pages, /get-started, /talk-to-us are mounted directly
 * by the host gate in main.tsx, so initPageViewTracking's router subscription
 * never fires there, the original reason the Website tab sat empty). These
 * pages navigate by full page load, so one call on mount is exactly one
 * pageview. Import React lazily-safe: this is a plain function to call from
 * a useEffect(() => trackFirstPartyPageviewOnMount(), []).
 */
export function trackFirstPartyPageviewOnMount(): void {
  try {
    sendFirstPartyPageview(window.location.pathname);
  } catch {
    /* best-effort */
  }
}

/**
 * First-party CLICK event (same collector), used by the marketing page's
 * CTAs so the platform "Website" tab shows engagement (get-started,
 * talk-to-us, referral), not just pageviews. Works for venue hosts too if a
 * venue page ever wants an explicit click signal.
 */
export function trackFirstPartyClick(label: string, href?: string): void {
  try {
    const slug = firstPartySlug();
    if (!slug) return;
    sendFirstPartyEvent(
      JSON.stringify({
        venue: slug,
        type: "click",
        path: window.location.pathname.split("?")[0] || "/",
        label,
        href,
      }),
    );
  } catch {
    /* best-effort */
  }
}

/**
 * Pushes a `virtual_page_view` (see this file's top comment for why that
 * name, not `page_view`). Defaults to the real browser location, this is
 * what `initPageViewTracking` calls on every router navigation, but accepts
 * an `overridePath` for views that change what the visitor is looking at
 * without a real navigation, e.g. the record-shop storefront's in-page
 * genre/era/sort/pagination controls (see `records.tsx`), which filter via
 * local state rather than the URL. Deduped against the last-pushed path
 * either way, so a real navigation and a synthetic call that happen to
 * resolve to the same conceptual view never double-count.
 */
export function pushVirtualPageView(overridePath?: string): void {
  if (typeof window === "undefined") return;
  const w = window as DataLayerWindow;
  const dl = w.dataLayer;
  if (!dl) return; // GTM not loaded, nothing to track (shouldn't happen; defensive)
  const path = overridePath ?? window.location.pathname + window.location.search;
  if (path === lastVirtualPageViewPath) return; // de-dupe repeat resolutions of the same view
  lastVirtualPageViewPath = path;
  // First-party pageview into Assembly's own store, the default for every
  // hosted venue (independent of GTM/GA4 below, which may be blocked/pending).
  sendFirstPartyPageview(path);
  const location = overridePath ? `${window.location.origin}${overridePath}` : window.location.href;
  const title = document.title;
  dl.push({
    event: "virtual_page_view",
    page_path: path,
    page_location: location,
    page_title: title,
    venue_slug: currentVenueSlug ?? undefined,
    venue_type: currentVenueType ?? undefined,
  });
  // Separate from the dataLayer push above (that one drives GTM's Custom
  // Event trigger), a client's own directly-connected GA4 property (see
  // loadGa4Property) isn't inside GTM, so it needs its own explicit gtag
  // page_view event, and unlike the dataLayer push above this one talks
  // straight to gtag.js, `page_view` here is GA4's real recognized event
  // name, not a GTM trigger-matched dataLayer signal, so it stays as-is.
  w.gtag?.("event", "page_view", { page_path: path, page_location: location, page_title: title });
}

export function initPageViewTracking(router: typeof appRouter): void {
  if (typeof window === "undefined") return;

  // Fire after each navigation fully resolves. rAF gives the route component a
  // frame to update document.title before we read it. Registered at startup so
  // the initial load's resolution is captured too.
  router.subscribe("onResolved", () => {
    requestAnimationFrame(() => pushVirtualPageView());
  });
}

const VISITOR_ID_KEY = "assembly_visitor_id";

// Anonymous per-browser id for the presence heartbeat below, not tied to
// any account, just persistent enough that a returning visitor upserts the
// same VenueVisitorHeartbeat row instead of minting a new one every tab.
function getVisitorId(): string | null {
  try {
    let id = localStorage.getItem(VISITOR_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(VISITOR_ID_KEY, id);
    }
    return id;
  } catch {
    return null; // localStorage unavailable (private mode etc.), skip heartbeats entirely
  }
}

const PRESENCE_PING_INTERVAL_MS = 20_000;

/**
 * First-party "people on site right now" heartbeat, pings the backend on
 * an interval while a venue page is open and visible (see
 * src/lib/handlers/venue-admin/web-analytics.ts's getRealtimeVisitors,
 * which counts recently-seen rows for the admin realtime badge). Gated on
 * the same analytics consent flag as GTM/GA4 above, so this first-party
 * signal doesn't become a consent loophole. Call once at boot, alongside
 * initPageViewTracking.
 */
export function initPresenceHeartbeat(): void {
  if (typeof window === "undefined") return;

  let timer: ReturnType<typeof setInterval> | null = null;

  const ping = () => {
    if (document.visibilityState !== "visible") return;
    if (!currentVenueSlug || !hasAnalyticsConsent()) return;
    const visitorId = getVisitorId();
    if (!visitorId) return;
    pingVenuePresence(currentVenueSlug, visitorId).catch(() => {
      // best-effort, a dropped ping just means this visitor isn't counted
      // until the next one
    });
  };

  const start = () => {
    if (timer) return;
    ping();
    timer = setInterval(ping, PRESENCE_PING_INTERVAL_MS);
  };
  const stop = () => {
    if (!timer) return;
    clearInterval(timer);
    timer = null;
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") start();
    else stop();
  });

  if (document.visibilityState === "visible") start();
}
